/**
 * Step 2 — Join a WhatsApp group (waha-connector-delta.md §3.1).
 *
 * Paste a group invite link → preview (join-info, no side effects) → confirm →
 * the shared Brain number joins the group, posts a consent announcement, and
 * binds the group to this workspace. Live messages then ingest via webhooks;
 * history comes from the export step (next).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Loader2, Phone, Trash2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { InfoNote } from '@/components/patterns/info-note';
import { StatusPill } from '@/components/patterns/status-pill';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type LinkedGroup, type WahaGroupPreview, wahaApi } from '@/lib/api';
import { BRAIN_WHATSAPP_NUMBER } from '@/lib/constants';
import { qk } from '@/lib/query-client';

interface Props {
  onNext: () => void;
}

const STATUS_LABEL: Record<LinkedGroup['status'], string> = {
  previewing: 'Previewing',
  pending_approval: 'Awaiting admin approval',
  joined: 'Joined',
  announced: 'Announced',
  active: 'Active',
  removed: 'Removed',
  conflict: 'Conflict',
};

/** A linked group's display name — never the bare JID when a title exists. */
function groupLabel(g: LinkedGroup): string {
  return g.name?.trim() || `Group ${g.groupJid.replace('@g.us', '')}`;
}

/** "Jul 30" — enough to place when Brain dropped out, without false precision. */
function formatRemovedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function StepLink({ onNext }: Props) {
  const qc = useQueryClient();
  const [inviteLink, setInviteLink] = useState('');
  const [preview, setPreview] = useState<WahaGroupPreview | null>(null);
  /** The group awaiting leave confirmation, if any. */
  const [confirmingLeave, setConfirmingLeave] = useState<LinkedGroup | null>(null);

  const { data: groups } = useQuery({
    queryKey: qk.wahaGroups,
    queryFn: () => wahaApi.groups(),
  });
  const activeGroups = (groups ?? []).filter((g) => g.status !== 'removed');
  // Groups Brain is out of — whether it left or an admin removed it. These used
  // to be filtered away entirely, so a group that stopped being indexed just
  // vanished from the screen with nothing said and no way back.
  const removedGroups = (groups ?? []).filter((g) => g.status === 'removed');

  const previewMut = useMutation({
    mutationFn: (link: string) => wahaApi.preview(link),
    onSuccess: (res) => setPreview(res),
    onError: () => toast.error("Couldn't read that invite link. Check it and try again."),
  });

  const linkMut = useMutation({
    mutationFn: (link: string) => wahaApi.link(link),
    onSuccess: (res) => {
      if (res.pendingApproval) {
        toast.info('Requested to join — waiting for a group admin to approve.');
      } else {
        toast.success(`Linked "${res.name}" — now indexing.`);
      }
      setPreview(null);
      setInviteLink('');
      void qc.invalidateQueries({ queryKey: qk.wahaGroups });
    },
    onError: (err) => {
      const status = (err as { status?: number }).status;
      if (status === 409) toast.error('That group is already linked to another workspace.');
      else toast.error('Failed to link the group. Please try again.');
    },
  });

  const unlinkMut = useMutation({
    mutationFn: (jid: string) => wahaApi.unlink(jid),
    onSuccess: () => {
      toast.success('Brain left the group and stopped indexing it.');
      setConfirmingLeave(null);
      void qc.invalidateQueries({ queryKey: qk.wahaGroups });
    },
    onError: () => toast.error('Failed to unlink.'),
  });

  const rejoinMut = useMutation({
    mutationFn: (jid: string) => wahaApi.rejoin(jid),
    onSuccess: (res) => {
      if (res.alreadyActive) toast.success('Brain is already in that group.');
      else if (res.pendingApproval)
        toast.info('Requested to rejoin — waiting for a group admin to approve.');
      else toast.success('Brain rejoined the group and is indexing it again.');
      void qc.invalidateQueries({ queryKey: qk.wahaGroups });
    },
    onError: (err) => {
      // A stored invite link can be revoked or rotated while Brain is out, so
      // point at the way forward instead of just reporting a failure.
      const status = (err as { status?: number }).status;
      if (status === 409)
        toast.error('That invite link no longer works — paste a new one above to reconnect.');
      else toast.error("Couldn't rejoin the group. Please try again.");
    },
  });

  return (
    <div className="space-y-6">
      {/* Join form */}
      <div className="rounded-xl border border-border p-8">
        <IconTile tone="neutral">
          <Users className="h-5 w-5" />
        </IconTile>
        <h2 className="mt-4 text-lg font-semibold">Join your group</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Paste your WhatsApp group&apos;s invite link and Brain will join, then start indexing
          messages.
        </p>

        {BRAIN_WHATSAPP_NUMBER && (
          <div className="mt-6 flex items-center gap-4 rounded-lg border border-border-subtle p-4">
            <IconTile tone="green" size={38}>
              <Phone className="h-4 w-4" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <p className="text-[12px] text-faint">Brain&apos;s WhatsApp number</p>
              <p className="font-mono text-sm">{BRAIN_WHATSAPP_NUMBER}</p>
            </div>
            <StatusPill tone="active">Active</StatusPill>
          </div>
        )}

        {/* Consent notice — R2, must be visible. */}
        <div className="mt-6 rounded-lg border border-info-border bg-info-bg p-4">
          <p className="text-[13.5px] font-semibold text-info">Let your group members know</p>
          <p className="mt-1 text-[13px] text-info/80">
            Once Brain joins, it stores and indexes all messages in the group for search. You must
            inform members that their messages are stored.
          </p>
        </div>

        {/* Paste + preview */}
        <div className="mt-6 space-y-1.5">
          <Label htmlFor="invite">WhatsApp group invite link</Label>
          <div className="flex gap-2">
            <Input
              id="invite"
              placeholder="https://chat.whatsapp.com/..."
              value={inviteLink}
              onChange={(e) => {
                setInviteLink(e.target.value);
                setPreview(null);
              }}
              className="font-mono text-xs"
            />
            <Button
              variant="outline"
              disabled={!inviteLink.trim() || previewMut.isPending}
              onClick={() => previewMut.mutate(inviteLink.trim())}
            >
              {previewMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview'}
            </Button>
          </div>
          <InfoNote>
            In WhatsApp: open the group → group name → Invite to Group via link → Copy link.
          </InfoNote>
        </div>

        {/* Preview result → confirm */}
        {preview && (
          <div className="mt-6 space-y-3 rounded-lg border border-border p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{preview.name || 'WhatsApp group'}</span>
              <span className="text-sm text-muted-foreground">
                · {preview.participantCount} member{preview.participantCount === 1 ? '' : 's'}
              </span>
            </div>

            {preview.conflict ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Already linked elsewhere</AlertTitle>
                <AlertDescription>
                  This group is linked to another workspace. A group can belong to only one
                  workspace.
                </AlertDescription>
              </Alert>
            ) : preview.alreadyLinked ? (
              <Alert variant="success">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Already linked here</AlertTitle>
                <AlertDescription>This group is already linked to your workspace.</AlertDescription>
              </Alert>
            ) : (
              <>
                {preview.joinApprovalRequired && (
                  <Alert variant="warning">
                    <Clock className="h-4 w-4" />
                    <AlertTitle>Admin approval required</AlertTitle>
                    <AlertDescription>
                      This group requires an admin to approve new members. We&apos;ll request to
                      join and start indexing once approved.
                    </AlertDescription>
                  </Alert>
                )}
                <Button
                  size="cta"
                  className="w-full"
                  disabled={linkMut.isPending}
                  onClick={() => linkMut.mutate(inviteLink.trim())}
                >
                  {linkMut.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Joining…
                    </>
                  ) : (
                    'Join group'
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Linked groups */}
      {activeGroups.length > 0 && (
        <div className="rounded-xl border border-border p-6">
          <h3 className="text-base font-semibold">Linked groups</h3>
          <div className="mt-4 space-y-2">
            {activeGroups.map((g) => (
              <div
                key={g.groupJid}
                className="flex items-center justify-between rounded-md border border-border-subtle px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-2 text-sm">
                  {g.status === 'pending_approval' ? (
                    <Clock className="h-4 w-4 shrink-0 text-warning" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                  )}
                  <span className="truncate">{groupLabel(g)}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    · {STATUS_LABEL[g.status]}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Leave ${groupLabel(g)}`}
                  disabled={unlinkMut.isPending}
                  onClick={() => setConfirmingLeave(g)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Groups Brain is no longer in */}
      {removedGroups.length > 0 && (
        <div className="rounded-xl border border-border p-6">
          <h3 className="text-base font-semibold">Not in this group anymore</h3>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Brain isn&apos;t in {removedGroups.length === 1 ? 'this group' : 'these groups'} and
            isn&apos;t indexing {removedGroups.length === 1 ? 'it' : 'them'}. Rejoining announces
            itself to the members again.
          </p>
          <div className="mt-4 space-y-2">
            {removedGroups.map((g) => (
              <div
                key={g.groupJid}
                className="flex items-center justify-between gap-3 rounded-md border border-border-subtle px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
                  <div className="min-w-0">
                    <p className="truncate">{groupLabel(g)}</p>
                    <p className="text-xs text-muted-foreground">
                      Brain is no longer in this group
                      {g.removedAt ? ` · ${formatRemovedAt(g.removedAt)}` : ''}
                    </p>
                  </div>
                </div>
                {g.canRejoin ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    disabled={rejoinMut.isPending}
                    onClick={() => rejoinMut.mutate(g.groupJid)}
                  >
                    {rejoinMut.isPending && rejoinMut.variables === g.groupJid ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Rejoining…
                      </>
                    ) : (
                      'Rejoin group'
                    )}
                  </Button>
                ) : (
                  // No stored code — the only way back is a fresh link.
                  <span className="shrink-0 text-xs text-muted-foreground">
                    Paste a new invite link above
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Button size="cta" onClick={onNext} className="w-full" disabled={activeGroups.length === 0}>
        {activeGroups.length === 0 ? 'Join a group to continue' : 'Continue'}
      </Button>

      {confirmingLeave && (
        <LeaveGroupDialog
          group={confirmingLeave}
          busy={unlinkMut.isPending}
          onCancel={() => setConfirmingLeave(null)}
          onConfirm={() => unlinkMut.mutate(confirmingLeave.groupJid)}
        />
      )}
    </div>
  );
}

/**
 * Confirmation for an irreversible action. Unlinking makes Brain LEAVE the
 * WhatsApp group — undoing it needs a fresh invite link from a group admin —
 * so it must not sit one unguarded click away.
 *
 * There is no shared Dialog primitive in this repo; the overlay and its dismiss
 * behaviour are built inline, matching MemberEditDialog and CreateOrgDialog.
 */
function LeaveGroupDialog({
  group,
  busy,
  onCancel,
  onConfirm,
}: {
  group: LinkedGroup;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // Escape dismisses — but never mid-request.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return createPortal(
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click-to-dismiss; Escape and Cancel are the keyboard paths
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-group-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-lg">
        <h2 id="leave-group-title" className="text-base font-semibold">
          Leave this group?
        </h2>
        <p className="mt-3 text-[13.5px] text-muted-foreground">
          Brain will leave <span className="font-medium text-foreground">{groupLabel(group)}</span>{' '}
          on WhatsApp and stop indexing it. Messages already indexed stay searchable.
        </p>
        <p className="mt-2 text-[13px] text-muted-foreground">
          To undo this, a group admin has to send a new invite link.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" size="sm" onClick={onConfirm} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Leave group'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
